import { Form, Formik, FormikProps } from 'formik';
import { Ref, useMemo, useRef } from 'react';
import { AnyObject } from 'yup';
import OnboardFormLayout from '../../components/OnboardFormLayout';
import OptionalTextDivider from '../../components/OptionalTextDivider';
import { useDivideSchemaFields } from '../../hooks/useDivideSchemaFields';
import { BaseFormControls, SlotProps } from '../GenericForm';
import { formValidator, useCRUDContext } from '../context/CRUDContext';
import { FieldsArea } from './components/FieldsArea';
import { FormFieldType } from './components/FormFieldType';
import GenericFormField from './components/GenericFormField';
import { SubmitButton } from './components/SubmitButton';
import { fixedForwardRef } from './utils/forwardRefFix';
import { generateCreateInitialValues } from './utils/generateCreateInitialValues';
import { useFormControllerRefSetup } from './utils/useFormControllerRefSetup';

function CreateFormInner<T extends AnyObject>(
  props: SlotProps<T>,
  ref?: Ref<BaseFormControls | null>
) {
  const crud = useCRUDContext();
  const formikRef = useRef<FormikProps<Partial<T>> | null>(null);
  useFormControllerRefSetup(ref, formikRef);

  const initialValues = useMemo(
    () => generateCreateInitialValues(props.fieldsConfig) as Partial<T>,
    [props.fieldsConfig]
  );

  const { requiredFields, optionalFields } = useDivideSchemaFields(
    crud.yupSchemaIn
  );

  const renderFormFields = (fieldNames: string[]) =>
    fieldNames
      .filter((key) => !props?.hiddenFields?.includes(key))
      .map((key) => {
        const fieldProps = props.fieldsConfig?.[key] || {
          type: FormFieldType.TextField,
          label: key,
          initialValue: '',
        };
        return <GenericFormField key={key} name={key} fieldMeta={fieldProps} />;
      });

  return (
    <Formik
      initialValues={initialValues}
      innerRef={formikRef}
      validate={(values) =>
        formValidator({
          value: values,
          schema: crud.yupSchemaIn,
          extraValidation: crud.extraValidation,
        })
      }
      onSubmit={async (value, helpers) =>
        crud.formSubmitHandler({
          helpers,
          operationCallback: async () => {
            return await crud.handleAdd(value);
          },
        })
      }
    >
      {(formikProps) => {
        return (
          <Form
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
            onSubmit={formikProps.handleSubmit}
            onReset={formikProps.handleReset}
          >
            <OnboardFormLayout
              onSubmit={formikProps.handleSubmit}
              formikErrors={formikProps.errors}
            >
              <FieldsArea {...props.gridConfig}>
                {requiredFields.length > 0 && renderFormFields(requiredFields)}
              </FieldsArea>
              {optionalFields.length > 0 && <OptionalTextDivider />}
              <FieldsArea {...props.gridConfig}>
                {optionalFields.length > 0 && renderFormFields(optionalFields)}
              </FieldsArea>

              {props.SubmitButton?.renderFn ? (
                props.SubmitButton.renderFn({
                  formikProps: formikProps,
                  config: props.SubmitButton?.config,
                })
              ) : (
                <SubmitButton
                  formikProps={formikProps}
                  config={props.SubmitButton?.config}
                />
              )}
            </OnboardFormLayout>
          </Form>
        );
      }}
    </Formik>
  );
}

export const CreateForm = fixedForwardRef(CreateFormInner);
